import { httpsCallable, HttpsCallableResult } from 'firebase/functions'
import { functions } from '@/config/firebase'
import type {
  SalesReportRequest,
  AggregatedReport,
  ApiResponse,
} from '@/lib/types'

export class FunctionsService {
  /**
   * Get sales report from Cloud Function
   */
  static async getSalesReport(
    from: Date | string | number | { toDate: () => Date },
    to: Date | string | number | { toDate: () => Date },
    forceShopify = false
  ): Promise<ApiResponse<AggregatedReport>> {
    try {
      const normalizeDate = (value: Date | string | number | { toDate: () => Date }): Date => {
        if (value instanceof Date) {
          return value
        }

        if (value && typeof (value as any).toDate === 'function') {
          const converted = (value as { toDate: () => Date }).toDate()
          if (converted instanceof Date && !Number.isNaN(converted.getTime())) {
            return converted
          }
        }

        const parsed =
          typeof value === 'string' || typeof value === 'number' ? new Date(value) : null

        if (parsed instanceof Date && !Number.isNaN(parsed.getTime())) {
          return parsed
        }

        throw new Error('Fecha inválida proporcionada al generar el reporte')
      }

      const fromDate = normalizeDate(from)
      const toDate = normalizeDate(to)

      const callable = httpsCallable<SalesReportRequest, AggregatedReport>(
        functions,
        'getSalesReport'
      )

      const result: HttpsCallableResult<AggregatedReport> = await callable({
        from: fromDate.toISOString(),
        to: toDate.toISOString(),
        forceShopify,
      })

      return {
        data: result.data,
        metadata: {
          timestamp: new Date().toISOString(),
          source: forceShopify ? 'shopify-api-forced' : 'cloud-function',
        },
      }
    } catch (error: any) {
      console.error('Error calling getSalesReport:', error)

      return {
        error: this.getFunctionErrorMessage(error),
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'error',
        },
      }
    }
  }

  /**
   * Save budget configuration to Firestore
   */
  static async saveBudgetConfig(budgetConfig: any): Promise<ApiResponse<{ success: boolean; message: string }>> {
    try {
      const callable = httpsCallable<{ budgetConfig: any }, { success: boolean; message: string }>(
        functions,
        'saveBudgetConfig'
      )

      const result = await callable({ budgetConfig })

      return {
        data: result.data,
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'cloud-function',
        },
      }
    } catch (error: any) {
      console.error('Error calling saveBudgetConfig:', error)

      return {
        error: this.getFunctionErrorMessage(error),
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'error',
        },
      }
    }
  }

  /**
   * Clear cache for a specific date range or all cache
   */
  static async clearCache(dateRange?: { from: Date; to: Date }): Promise<ApiResponse<{ success: boolean; message: string }>> {
    try {
      const callable = httpsCallable<{ dateRange?: { from: string; to: string } }, { success: boolean; message: string }>(
        functions,
        'clearCache'
      )

      const payload = dateRange
        ? {
            dateRange: {
              from: dateRange.from.toISOString(),
              to: dateRange.to.toISOString(),
            },
          }
        : {}

      const result = await callable(payload)

      return {
        data: result.data,
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'cloud-function',
        },
      }
    } catch (error: any) {
      console.error('Error calling clearCache:', error)

      return {
        error: this.getFunctionErrorMessage(error),
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'error',
        },
      }
    }
  }

  /**
   * Get human-readable error messages
   */
  private static getFunctionErrorMessage(error: any): string {
    if (error.code === 'unauthenticated') {
      return 'Debes iniciar sesión para acceder a los reportes'
    }

    if (error.code === 'permission-denied') {
      return 'No tienes permisos para acceder a este recurso'
    }

    if (error.code === 'unavailable') {
      return 'El servicio no está disponible. Intenta más tarde'
    }

    if (error.code === 'deadline-exceeded') {
      return 'La consulta tardó demasiado. Intenta con un rango menor'
    }

    if (error.message) {
      return error.message
    }

    return 'Error al obtener el reporte. Intenta de nuevo'
  }

  /**
   * Fetch product details (images, handle, etc.) for a set of product IDs
   */
  static async getProductsByIds(
    productIds: string[]
  ): Promise<
    ApiResponse<
      Record<
        string,
        {
          imageUrl?: string | null
          handle?: string | null
          title?: string | null
          inventory?: number | null
          createdAt?: string | null
        }
      >
    >
  > {
    if (!Array.isArray(productIds) || productIds.length === 0) {
      return {
        data: {},
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'client-cache',
        },
      }
    }

    try {
      const callable = httpsCallable<
        { productIds: string[] },
        Record<string, { imageUrl?: string | null; handle?: string | null; title?: string | null; createdAt?: string | null }>
      >(functions, 'getProductsByIds')

      const result = await callable({ productIds })

      return {
        data: result.data ?? {},
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'cloud-function',
        },
      }
    } catch (error: any) {
      console.error('Error calling getProductsByIds:', error)
      return {
        error: this.getFunctionErrorMessage(error),
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'error',
        },
      }
    }
  }

  static async getAllProducts(): Promise<ApiResponse<{ id: string; title: string; inventory: number; createdAt?: string | null; handle?: string | null; imageUrl?: string | null; status?: string; collections?: string[]; cost?: number }[]>> {
    try {
      const callable = httpsCallable<void, { id: string; title: string; inventory: number; createdAt?: string | null; handle?: string | null; imageUrl?: string | null; status?: string; collections?: string[]; cost?: number }[]>(
        functions,
        'getAllProducts',
        {
          timeout: 540000, // 9 minutes timeout (same as backend function)
        }
      );

      const result = await callable();

      return {
        data: result.data,
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'cloud-function',
        },
      };
    } catch (error: any) {
      console.error('Error calling getAllProducts:', error);

      return {
        error: this.getFunctionErrorMessage(error),
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'error',
        },
      };
    }
  }

  static async getProductsFromFirestore(): Promise<ApiResponse<{ products: any[]; metadata: { lastSync: Date | null; totalProducts: number } }>> {
    try {
      const callable = httpsCallable<void, { products: any[]; metadata: { lastSync: Date | null; totalProducts: number } }>(
        functions,
        'getProductsFromFirestore'
      );

      const result = await callable();

      return {
        data: result.data,
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'firestore',
        },
      };
    } catch (error: any) {
      console.error('Error calling getProductsFromFirestore:', error);

      return {
        error: this.getFunctionErrorMessage(error),
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'error',
        },
      };
    }
  }

  static async getProductById(productIdOrTitle: string): Promise<ApiResponse<any>> {
    try {
      const callable = httpsCallable<{ productIdOrTitle: string }, any>(
        functions,
        'getProductById'
      );

      const result = await callable({ productIdOrTitle });

      return {
        data: result.data,
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'shopify-api',
        },
      };
    } catch (error: any) {
      console.error('Error calling getProductById:', error);

      return {
        error: this.getFunctionErrorMessage(error),
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'error',
        },
      };
    }
  }

  static async syncProductsToFirestore(): Promise<ApiResponse<{ success: boolean; totalProducts: number; productsWithCost: number; productsWithCollections: number }>> {
    try {
      const callable = httpsCallable<void, { success: boolean; totalProducts: number; productsWithCost: number; productsWithCollections: number }>(
        functions,
        'syncProductsToFirestore',
        {
          timeout: 540000, // 9 minutes timeout (same as backend function)
        }
      );

      const result = await callable();

      return {
        data: result.data,
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'cloud-function',
        },
      };
    } catch (error: any) {
      console.error('Error calling syncProductsToFirestore:', error);

      return {
        error: this.getFunctionErrorMessage(error),
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'error',
        },
      };
    }
  }

  static async importProductsFromJSON(products: any[]): Promise<ApiResponse<{ success: boolean; totalProducts: number; productsWithCost: number; message: string }>> {
    try {
      const callable = httpsCallable<{ products: any[] }, { success: boolean; totalProducts: number; productsWithCost: number; message: string }>(
        functions,
        'importProductsFromJSON',
        {
          timeout: 540000, // 9 minutes timeout
        }
      );

      const result = await callable({ products });

      return {
        data: result.data,
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'cloud-function',
        },
      };
    } catch (error: any) {
      console.error('Error calling importProductsFromJSON:', error);

      return {
        error: this.getFunctionErrorMessage(error),
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'error',
        },
      };
    }
  }

  static async getProductsByCollection(data: { collectionId?: string; collectionTitle?: string }): Promise<ApiResponse<any>> {
    try {
      const callable = httpsCallable<{ collectionId?: string; collectionTitle?: string }, any>(
        functions,
        'getProductsByCollection',
        {
          timeout: 540000, // 9 minutes timeout
        }
      );

      const result = await callable(data);

      return {
        data: result.data,
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'shopify-api',
        },
      };
    } catch (error: any) {
      console.error('Error calling getProductsByCollection:', error);

      return {
        error: this.getFunctionErrorMessage(error),
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'error',
        },
      };
    }
  }

  static async getSellers(): Promise<ApiResponse<{ sellers: Array<{ id: string; name: string; shortName?: string | null; role: string; percentage: number; hasCommission: boolean; active?: boolean }> }>> {
    try {
      const callable = httpsCallable<void, { sellers: Array<{ id: string; name: string; shortName?: string | null; role: string; percentage: number; hasCommission: boolean; active?: boolean }> }>(
        functions,
        'getSellers'
      );
      const result = await callable();
      return { data: result.data, metadata: { timestamp: new Date().toISOString(), source: 'firestore' } };
    } catch (error: any) {
      return { error: this.getFunctionErrorMessage(error), metadata: { timestamp: new Date().toISOString(), source: 'error' } };
    }
  }

  static async saveSeller(seller: { id: string; name: string; shortName?: string; role: string; percentage: number; hasCommission: boolean; active?: boolean; previousId?: string }): Promise<ApiResponse<{ success: boolean; message: string }>> {
    try {
      const callable = httpsCallable<typeof seller, { success: boolean; message: string }>(
        functions,
        'saveSeller'
      );
      const result = await callable(seller);
      return { data: result.data, metadata: { timestamp: new Date().toISOString(), source: 'firestore' } };
    } catch (error: any) {
      return { error: this.getFunctionErrorMessage(error), metadata: { timestamp: new Date().toISOString(), source: 'error' } };
    }
  }

  static async deleteSeller(id: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
    try {
      const callable = httpsCallable<{ id: string }, { success: boolean; message: string }>(
        functions,
        'deleteSeller'
      );
      const result = await callable({ id });
      return { data: result.data, metadata: { timestamp: new Date().toISOString(), source: 'firestore' } };
    } catch (error: any) {
      return { error: this.getFunctionErrorMessage(error), metadata: { timestamp: new Date().toISOString(), source: 'error' } };
    }
  }

  static async clearProductsCache(): Promise<ApiResponse<{ success: boolean; deletedProducts: number; message: string }>> {
    try {
      const callable = httpsCallable<void, { success: boolean; deletedProducts: number; message: string }>(
        functions,
        'clearProductsCache'
      );

      const result = await callable();

      return {
        data: result.data,
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'cloud-function',
        },
      };
    } catch (error: any) {
      console.error('Error calling clearProductsCache:', error);

      return {
        error: this.getFunctionErrorMessage(error),
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'error',
        },
      };
    }
  }
}
